import type {
  Vector3Tuple,
  VisualContentBudget,
  VisualContentCatalog,
  VisualContentCategory,
  VisualContentEntry,
  VisualContentMetrics,
} from "../config/catalog-schema";

const CATEGORY_LIMITS: Readonly<Record<VisualContentCategory, VisualContentBudget>> = {
  ground: {
    maxCompressedBytes: 3 * 1024 * 1024,
    maxTriangles: 20_000,
    maxMaterials: 4,
    maxTextureSize: 1024,
  },
  environment: {
    maxCompressedBytes: 6 * 1024 * 1024,
    maxTriangles: 60_000,
    maxMaterials: 8,
    maxTextureSize: 1024,
  },
};

export interface BudgetEvaluation {
  readonly accepted: boolean;
  readonly violations: readonly string[];
}

export function parseVisualContentCatalog(value: unknown): VisualContentCatalog {
  if (!isRecord(value) || value.schemaVersion !== 1 || typeof value.catalogVersion !== "string") {
    throw new Error("Visual catalog header is invalid");
  }
  if (!Array.isArray(value.entries) || value.entries.length === 0) {
    throw new Error("Visual catalog must contain entries");
  }

  const entries = value.entries.map((entry, index) => parseEntry(entry, index));
  const ids = new Set<string>();
  for (const entry of entries) {
    if (ids.has(entry.id)) throw new Error(`Duplicate visual content id: ${entry.id}`);
    ids.add(entry.id);
    validateConfiguredBudget(entry);
    const declared = evaluateContentBudget(entry.declaredMetrics, entry.budget);
    if (!declared.accepted) {
      throw new Error(`${entry.id} declared metrics exceed budget: ${declared.violations.join(", ")}`);
    }
  }
  return { schemaVersion: 1, catalogVersion: value.catalogVersion, entries };
}

export function evaluateContentBudget(
  metrics: VisualContentMetrics,
  budget: VisualContentBudget,
): BudgetEvaluation {
  const violations: string[] = [];
  if (metrics.compressedBytes > budget.maxCompressedBytes) violations.push("compressed_bytes");
  if (metrics.triangles > budget.maxTriangles) violations.push("triangles");
  if (metrics.materials > budget.maxMaterials) violations.push("materials");
  if (metrics.maxTextureSize > budget.maxTextureSize) violations.push("texture_size");
  if (metrics.skeletalAnimations > 0) violations.push("skeletal_animation");
  return { accepted: violations.length === 0, violations };
}

function parseEntry(value: unknown, index: number): VisualContentEntry {
  if (!isRecord(value)) throw new Error(`Visual catalog entry ${index} is invalid`);
  const category = value.category;
  const source = value.source;
  if (category !== "ground" && category !== "environment") throw new Error(`Entry ${index} has invalid category`);
  if (source !== "procedural_placeholder" && source !== "glb") throw new Error(`Entry ${index} has invalid source`);
  const id = requireString(value.id, `Entry ${index} id`);
  if (!id.startsWith(`${category}.`)) throw new Error(`${id} does not match category ${category}`);
  const modelUrl = value.modelUrl;
  if (source === "glb" && (typeof modelUrl !== "string" || !modelUrl.toLowerCase().endsWith(".glb"))) {
    throw new Error(`${id} requires a .glb modelUrl`);
  }
  if (source === "procedural_placeholder" && modelUrl !== null) {
    throw new Error(`${id} procedural content must use a null modelUrl`);
  }
  if (!isRecord(value.transform) || !isRecord(value.lod) || !isRecord(value.budget) || !isRecord(value.declaredMetrics)) {
    throw new Error(`${id} is missing transform, LOD, budget, or declared metrics`);
  }
  const scale = parseVector(value.transform.scale, `${id} scale`);
  if (scale.some((component) => component <= 0)) throw new Error(`${id} scale must be positive`);
  if (value.lod.mode !== "single" || !isPositiveNumber(value.lod.maxDistance)) throw new Error(`${id} LOD is invalid`);
  if (!isRecord(value.materialOverrides) || !isStringArray(value.compatibleEnvironments) || !isStringArray(value.audioSlots)) {
    throw new Error(`${id} compatibility or material metadata is invalid`);
  }
  if (typeof value.instanced !== "boolean" || typeof value.enabled !== "boolean") throw new Error(`${id} flags are invalid`);

  return {
    id,
    displayName: requireString(value.displayName, `${id} displayName`),
    category,
    source,
    modelUrl: modelUrl as string | null,
    transform: {
      scale,
      rotationDegrees: parseVector(value.transform.rotationDegrees, `${id} rotationDegrees`),
      anchor: parseVector(value.transform.anchor, `${id} anchor`),
    },
    lod: { mode: "single", maxDistance: value.lod.maxDistance },
    instanced: value.instanced,
    materialOverrides: value.materialOverrides as Record<string, string>,
    compatibleEnvironments: value.compatibleEnvironments,
    audioSlots: value.audioSlots,
    enabled: value.enabled,
    version: requireString(value.version, `${id} version`),
    budget: parseBudget(value.budget, `${id} budget`),
    declaredMetrics: parseMetrics(value.declaredMetrics, `${id} declaredMetrics`),
  };
}

function validateConfiguredBudget(entry: VisualContentEntry): void {
  const limit = CATEGORY_LIMITS[entry.category];
  const checks: readonly (keyof VisualContentBudget)[] = [
    "maxCompressedBytes", "maxTriangles", "maxMaterials", "maxTextureSize",
  ];
  for (const key of checks) {
    if (entry.budget[key] > limit[key]) throw new Error(`${entry.id} configured ${key} exceeds the category ceiling`);
  }
}

function parseBudget(value: Record<string, unknown>, label: string): VisualContentBudget {
  return {
    maxCompressedBytes: positiveInteger(value.maxCompressedBytes, `${label}.maxCompressedBytes`),
    maxTriangles: positiveInteger(value.maxTriangles, `${label}.maxTriangles`),
    maxMaterials: positiveInteger(value.maxMaterials, `${label}.maxMaterials`),
    maxTextureSize: positiveInteger(value.maxTextureSize, `${label}.maxTextureSize`),
  };
}

function parseMetrics(value: Record<string, unknown>, label: string): VisualContentMetrics {
  return {
    compressedBytes: nonNegativeInteger(value.compressedBytes, `${label}.compressedBytes`),
    triangles: nonNegativeInteger(value.triangles, `${label}.triangles`),
    materials: nonNegativeInteger(value.materials, `${label}.materials`),
    maxTextureSize: nonNegativeInteger(value.maxTextureSize, `${label}.maxTextureSize`),
    skeletalAnimations: nonNegativeInteger(value.skeletalAnimations, `${label}.skeletalAnimations`),
  };
}

function parseVector(value: unknown, label: string): Vector3Tuple {
  if (!Array.isArray(value) || value.length !== 3 || !value.every((item) => typeof item === "number" && Number.isFinite(item))) {
    throw new Error(`${label} must have three finite numbers`);
  }
  return [value[0] as number, value[1] as number, value[2] as number];
}

function requireString(value: unknown, label: string): string {
  if (typeof value !== "string" || value.trim() === "") throw new Error(`${label} must be a non-empty string`);
  return value;
}

function positiveInteger(value: unknown, label: string): number {
  if (!Number.isInteger(value) || (value as number) <= 0) throw new Error(`${label} must be a positive integer`);
  return value as number;
}

function nonNegativeInteger(value: unknown, label: string): number {
  if (!Number.isInteger(value) || (value as number) < 0) throw new Error(`${label} must be a non-negative integer`);
  return value as number;
}

function isPositiveNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
